<template>
  <a-modal
    :open="visible"
    :title="`转移剩余时间 · 节点 #${source?.proxy_id ?? ''}`"
    :confirm-loading="submitting"
    :ok-button-props="{ danger: true, disabled: !selectedTargetID || remainingSeconds <= 0 }"
    ok-text="确认转移并删除源节点"
    cancel-text="取消"
    width="620px"
    @ok="onSubmit"
    @cancel="$emit('update:visible', false)"
  >
    <a-alert
      type="warning"
      show-icon
      message="转移成功后，源节点会立即删除并停止服务，此操作不可撤销。"
      style="margin-bottom:16px"
    />

    <a-descriptions :column="1" size="small" bordered style="margin-bottom:16px">
      <a-descriptions-item label="源节点">
        #{{ source?.proxy_id }} · {{ source?.username }} · {{ source?.node_name || '未知服务器' }}
      </a-descriptions-item>
      <a-descriptions-item label="源节点到期">
        {{ formatDateTime(source?.expire_at) }}
      </a-descriptions-item>
      <a-descriptions-item label="可转移时间">
        <strong :style="{ color: remainingSeconds > 0 ? '#1677ff' : '#ff4d4f' }">
          {{ formatDuration(remainingSeconds) }}
        </strong>
      </a-descriptions-item>
    </a-descriptions>

    <a-form layout="vertical">
      <a-form-item label="目标节点" required>
        <a-select
          v-model:value="selectedTargetID"
          show-search
          option-filter-prop="label"
          :loading="loadingTargets"
          :options="targetOptions"
          placeholder="请选择接收剩余时间的目标节点"
          style="width:100%"
        />
      </a-form-item>
    </a-form>

    <a-descriptions v-if="selectedTarget" :column="1" size="small" bordered>
      <a-descriptions-item label="目标当前到期">
        {{ formatDateTime(selectedTarget.expire_at) }}
      </a-descriptions-item>
      <a-descriptions-item label="预计转移后到期">
        <strong style="color:#52c41a">{{ formatDateTime(targetExpirePreview) }}</strong>
      </a-descriptions-item>
    </a-descriptions>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { listProxies, transferProxyTime, type TransferProxyTimeResponse } from '../api/proxies';
import type { ProxyItem } from '../types';
import { eligibleTransferTargets, remainingTransferSeconds } from './proxyTimeTransfer';

const props = defineProps<{
  visible: boolean;
  source: ProxyItem | null;
}>();

const emit = defineEmits<{
  (event: 'update:visible', value: boolean): void;
  (event: 'success', payload: TransferProxyTimeResponse): void;
}>();

const targets = ref<ProxyItem[]>([]);
const selectedTargetID = ref<number | null>(null);
const loadingTargets = ref(false);
const submitting = ref(false);
const openedAt = ref(new Date());
const secondsPerMinute = 60;
const secondsPerHour = 60 * secondsPerMinute;
const secondsPerDay = 24 * secondsPerHour;

const remainingSeconds = computed(() =>
  props.source ? remainingTransferSeconds(props.source.expire_at, openedAt.value) : 0,
);

const selectedTarget = computed(() =>
  targets.value.find((proxy) => proxy.proxy_id === selectedTargetID.value) ?? null,
);

const targetOptions = computed(() => targets.value.map((proxy) => ({
  value: proxy.proxy_id,
  label: `#${proxy.proxy_id} · ${proxy.username} · ${proxy.proxy_user_wechat_nickname || '未归属'} · ${proxy.node_name}`,
})));

const targetExpirePreview = computed(() => {
  if (!selectedTarget.value || remainingSeconds.value <= 0) return '';
  const targetExpireMs = new Date(selectedTarget.value.expire_at).getTime();
  if (!Number.isFinite(targetExpireMs)) return '';
  const baseMs = Math.max(targetExpireMs, openedAt.value.getTime());
  return new Date(baseMs + remainingSeconds.value * 1000).toISOString();
});

const formatDateTime = (value?: string) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('zh-CN', { hour12: false });
};

const formatDuration = (seconds: number) => {
  if (seconds <= 0) return '无可转移时间';
  const days = Math.floor(seconds / secondsPerDay);
  const hours = Math.floor((seconds % secondsPerDay) / secondsPerHour);
  const minutes = Math.floor((seconds % secondsPerHour) / secondsPerMinute);
  return `${days} 天 ${hours} 小时 ${minutes} 分钟`;
};

const loadTargets = async () => {
  const sourceID = props.source?.proxy_id;
  if (!sourceID) return;
  loadingTargets.value = true;
  try {
    const response = await listProxies({ status: 'active' });
    targets.value = eligibleTransferTargets(response.data as ProxyItem[], sourceID);
  } catch {
    targets.value = [];
    message.error('加载目标节点失败');
  } finally {
    loadingTargets.value = false;
  }
};

watch(
  () => props.visible,
  (visible) => {
    if (!visible) return;
    openedAt.value = new Date();
    selectedTargetID.value = null;
    targets.value = [];
    void loadTargets();
  },
);

const onSubmit = async () => {
  const sourceID = props.source?.proxy_id;
  const targetID = selectedTargetID.value;
  if (!sourceID || !targetID) {
    message.error('请选择目标节点');
    return;
  }
  submitting.value = true;
  try {
    const response = await transferProxyTime(sourceID, targetID);
    emit('success', response.data);
    emit('update:visible', false);
  } catch (error: unknown) {
    const apiError = error as { response?: { data?: { error?: { message?: string } } } };
    message.error(apiError.response?.data?.error?.message || '转移失败');
  } finally {
    submitting.value = false;
  }
};
</script>
